import * as ed from '@noble/ed25519'
import { randomBytes, utf8ToBytes } from '@noble/hashes/utils'

/**
 * The claim a live origin returns from `GET /_vn/beacon?nonce=…`. The client trusts a
 * candidate domain ONLY after this payload verifies against the pinned public key AND
 * echoes the exact random `nonce` the client just issued — never on the basis that "the
 * domain resolved" (DNS poisoning routinely hands back a live but hostile IP).
 *
 * The nonce is what makes the beacon a challenge-response rather than a static claim:
 * without it a genuine, still-fresh beacon captured from the real origin could be
 * replayed by a hostile poisoned IP for the whole skew window. Binding the signature to
 * a per-request random value forces any impostor to obtain a fresh signature for THIS
 * exchange — which the pinned private key never leaves the real origin to grant.
 *
 * Residual limitation (documented, not solved here): a live on-path relay that proxies
 * the client's nonce to the real origin and returns the genuine signed reply still passes.
 * The primary defense against that is ordinary TLS certificate validation on the API
 * connection; the beacon is the secondary gate that additionally rejects sinkholes,
 * block-pages and any origin that cannot produce a pinned-key signature for our nonce.
 */
export interface BeaconPayload {
  /** The domain this origin asserts it is serving. Must match where we connected. */
  domain: string
  /** Authoritative DGA index of `domain`, so the client can persist its position. */
  index: number
  /** Issue time in unix ms, checked against a skew window as a secondary replay bound. */
  issuedAt: number
  /** The client's per-request random challenge, echoed back and signed. Binds this exchange. */
  nonce: string
  /** Beacon format version. */
  v: number
}

export interface BeaconResponse {
  data: BeaconPayload
  /** base64url ed25519 signature over the canonical bytes of `data`. */
  sig: string
}

// Fixed field order — the canonical form both sides sign/verify. Independent of
// object key insertion order so client and server always agree byte-for-byte.
const FIELDS: (keyof BeaconPayload)[] = ['v', 'domain', 'index', 'issuedAt', 'nonce']

/** Deterministic serialization of a beacon payload to the exact bytes that get signed. */
export const canonicalBeaconBytes = (payload: BeaconPayload): Uint8Array =>
  utf8ToBytes(JSON.stringify(FIELDS.map((f) => payload[f])))

const bytesToBase64url = (bytes: Uint8Array): string => {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Decode a base64url string to bytes (portable across Node and the browser). */
export const base64urlToBytes = (s: string): Uint8Array => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}

/**
 * Generate a fresh, unpredictable challenge the client sends with each beacon request.
 * 16 bytes of CSPRNG output (portable across Node and the browser via `@noble/hashes`).
 */
export const generateNonce = (): string => bytesToBase64url(randomBytes(16))

/** Sign a beacon payload (server side, holds the private key). Returns a base64url signature. */
export const signBeacon = async (payload: BeaconPayload, privateKey: Uint8Array): Promise<string> =>
  bytesToBase64url(await ed.signAsync(canonicalBeaconBytes(payload), privateKey))

/** Verify a beacon signature against the pinned public key. */
export const verifyBeacon = async (payload: BeaconPayload, sig: string, publicKey: Uint8Array): Promise<boolean> => {
  try {
    return await ed.verifyAsync(base64urlToBytes(sig), canonicalBeaconBytes(payload), publicKey)
  } catch {
    // Malformed base64/signature bytes are just an invalid signature, not a crash.
    return false
  }
}

export interface VerifyBeaconOptions {
  /** The domain the client actually connected to. */
  expectedDomain: string
  /** The exact nonce this client generated for this request (the challenge it must echo). */
  expectedNonce: string
  /** Allowed clock skew / beacon age in ms. Default 2 minutes. */
  maxSkewMs?: number
  /** Current time in unix ms (injected, so this stays pure). */
  now: number
  /** Pinned ed25519 public key baked into the client. */
  publicKey: Uint8Array
}

export type VerifyBeaconResult =
  | { ok: false; reason: 'bad-signature' | 'domain-mismatch' | 'malformed' | 'nonce-mismatch' | 'stale' }
  | { ok: true; payload: BeaconPayload }

const DEFAULT_MAX_SKEW_MS = 2 * 60 * 1000

const isWellFormed = (res: BeaconResponse): boolean =>
  !!res &&
  typeof res.sig === 'string' &&
  res.sig.length > 0 &&
  !!res.data &&
  typeof res.data.domain === 'string' &&
  typeof res.data.nonce === 'string' &&
  Number.isFinite(res.data.issuedAt) &&
  Number.isFinite(res.data.index) &&
  Number.isFinite(res.data.v)

/**
 * Full client-side trust-gate for a beacon response. Verifies the signature FIRST
 * (nothing in the payload is trusted until it does), then that the signed domain is the
 * one we connected to, then that the echoed nonce is the challenge we just issued, then
 * that the beacon is fresh. Only an `ok: true` result may cause the client to adopt the
 * domain as its working API.
 */
export const verifyBeaconResponse = async (
  res: BeaconResponse,
  opts: VerifyBeaconOptions,
): Promise<VerifyBeaconResult> => {
  if (!isWellFormed(res)) {
    return { ok: false, reason: 'malformed' }
  }

  if (!(await verifyBeacon(res.data, res.sig, opts.publicKey))) {
    return { ok: false, reason: 'bad-signature' }
  }

  if (res.data.domain !== opts.expectedDomain) {
    return { ok: false, reason: 'domain-mismatch' }
  }

  if (res.data.nonce !== opts.expectedNonce) {
    return { ok: false, reason: 'nonce-mismatch' }
  }

  const maxSkew = opts.maxSkewMs ?? DEFAULT_MAX_SKEW_MS
  if (Math.abs(opts.now - res.data.issuedAt) > maxSkew) {
    return { ok: false, reason: 'stale' }
  }

  return { ok: true, payload: res.data }
}
